// Sign-out confirmation with per-bucket "keep / clear" checkboxes.
// Addresses Cobalt finding PT38031_6 (residual sensitive data in
// browser storage after sign-out). The user picks which local data
// to wipe before WorkOS revokes the session; their selections persist
// across sessions so the next sign-out remembers their choices.
//
// Built directly on OverlayDialog rather than ConfirmDialog because
// ConfirmDialog doesn't accept arbitrary children between body and
// buttons - widening it for one caller wasn't worth the surface.
//
// Auth tokens are always cleared inside authBridge.logout(); that
// behavior is non-negotiable and outside the checkbox grid.
//
// Portaled to document.body: the trigger lives in SidebarFooter, whose
// <aside> ancestor declares `contain: content`. CSS containment makes
// that ancestor a containing block for `position: fixed` descendants,
// so an un-portaled OverlayDialog would anchor to the 300px sidebar
// and overflow it. The portal lifts the dialog out so `fixed inset-0`
// resolves against the viewport.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import OverlayDialog from "./OverlayDialog.jsx";
import { logout } from "../lib/authBridge.js";
import { suppressFurtherWrites as suppressChatWrites } from "../lib/chatStorage.js";
import {
    CHAT_DATA_CLEAR_COPY,
    clearChatData,
    clearWorkflowData,
    clearMcpCredentials,
    loadLogoutPrefs,
    saveLogoutPrefs,
} from "../lib/clearUserData.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";

export default function LogoutDialog({ open, onClose }) {
    const [prefs, setPrefs] = useState(() => loadLogoutPrefs());
    const [busy, setBusy] = useState(false);

    // Re-read prefs every time the dialog opens so a parallel tab that
    // changed them gets picked up. Cheap (one localStorage read).
    useEffect(() => {
        if (open) {
            setPrefs(loadLogoutPrefs());
            setBusy(false);
        }
    }, [open]);

    const setField = (key) => (e) =>
        setPrefs((p) => ({ ...p, [key]: e.target.checked }));

    const onSignOut = async () => {
        if (busy) return;
        setBusy(true);
        saveLogoutPrefs(prefs);
        // Belt-and-suspenders kill switch for the WorkOS-redirect
        // window: between the awaited wipes and logout()'s nav,
        // ChatConsole's window.beforeunload handler will fire and
        // try to flush the in-memory chat state. The subscribe/notify
        // path inside clearChatData already empties that state, but
        // we suppress here too in case the user unchecked Clear chat
        // history (notify wouldn't fire) AND has stale rows that the
        // unload-flush would otherwise restore. Page is about to
        // navigate away, nothing legitimate left to persist.
        if (prefs.clearChat) suppressChatWrites();
        // No tenantId passed: clearUserData resolves from /v1/me's cached
        // result. authBridge's getTenantId() reads a localStorage key that
        // the current dashboard never writes, so it always returns null
        // and would silently retarget the wipes at "default".
        const tasks = [];
        if (prefs.clearChat)      tasks.push(clearChatData());
        if (prefs.clearWorkflows) tasks.push(clearWorkflowData());
        if (prefs.clearMcp)       clearMcpCredentials(); // sync
        // allSettled: a single failing wipe must not block sign-out.
        // Auth tokens still get cleared inside logout().
        await Promise.allSettled(tasks);
        logout();
    };

    if (!open || typeof document === "undefined") return null;
    return createPortal(
        <OverlayDialog
            open={open}
            onClose={busy ? undefined : onClose}
            dismissible={!busy}
            analyticsId={DIALOG.SIGN_OUT}
            eyebrow="Sign out"
            title="Sign out of Origin"
            width="460px"
        >
            <div className="grid gap-[14px]">
                <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">
                    Auth tokens are always cleared. Anything you leave checked below
                    is wiped from this browser before sign-out; anything unchecked
                    stays for the next sign-in.
                </p>
                <fieldset className="grid gap-[8px] border-0 p-0 m-0">
                    <CheckRow
                        checked={prefs.clearChat}
                        onChange={setField("clearChat")}
                        label="Clear chat history"
                        hint={CHAT_DATA_CLEAR_COPY.logoutHint}
                        disabled={busy}
                    />
                    <CheckRow
                        checked={prefs.clearWorkflows}
                        onChange={setField("clearWorkflows")}
                        label="Clear workflows"
                        hint="Workflow definitions and run history."
                        disabled={busy}
                    />
                    <CheckRow
                        checked={prefs.clearMcp}
                        onChange={setField("clearMcp")}
                        label="Clear MCP credentials"
                        hint="OAuth tokens and API keys for Linear, Notion, Zapier, and other MCP servers."
                        disabled={busy}
                    />
                </fieldset>
                <div className="flex items-center justify-end gap-[8px]">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted-soft)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:opacity-50 cursor-pointer"
                    >
                        Stay signed in
                    </button>
                    <button
                        type="button"
                        onClick={onSignOut}
                        autoFocus
                        disabled={busy}
                        className="h-[30px] rounded-[2px] border border-[color:var(--red,#c0392b)] bg-transparent px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--red,#c0392b)] font-medium hover:bg-[color:var(--red,#c0392b)] hover:text-[color:var(--ink)] disabled:opacity-50 cursor-pointer"
                    >
                        {busy ? "Signing out…" : "Sign out"}
                    </button>
                </div>
            </div>
        </OverlayDialog>,
        document.body,
    );
}

function CheckRow({ checked, onChange, label, hint, disabled }) {
    return (
        <label className="flex cursor-pointer items-start gap-[8px] rounded-[2px] border border-[color:var(--line)] px-[10px] py-[8px] hover:border-[color:var(--line-strong)]">
            <input
                type="checkbox"
                checked={checked}
                onChange={onChange}
                disabled={disabled}
                className="mt-[2px] h-[13px] w-[13px] flex-none cursor-pointer accent-[color:var(--green)]"
            />
            <span className="grid gap-[2px] min-w-0">
                <span className="text-[12px] leading-[1.2] text-[color:var(--ink)]">{label}</span>
                <span className="text-[10px] leading-[1.3] text-[color:var(--muted)]">{hint}</span>
            </span>
        </label>
    );
}
